/* eslint-disable react/no-danger */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import {
  FormattedMessage,
  FormattedDate,
  FormattedNumber,
  injectIntl,
} from 'react-intl';
import s from './Basket.scss';

import {
  ICONS,
  PAYMENT_METHODS,
  ACCOUNT_TYPE,
  ACCOUNT_PATH,
  TERMS_AND_CONDITIONS_PATH,
  DEPARTMENT_USER_STATUS,
  DELIVERY_METHODS,
  PRODUCT_TYPE,
  REDEEM_PATH,
  SALES_CHANNELS,
  SALES_CHANNELS_GROUP,
  MOCK_TEXT,
} from '../../constants';

import { trimStringProperties } from '../../helpers/text';
import {
  checkoutEvent,
  addToCartEvent,
  removeFromCartEvent,
} from '../../helpers/googleTagManager';

import withIntersectionObserver from '../../decorators/withIntersectionObserver';
import withResponsiveWrapper from '../../decorators/withResponsiveWrapper';
import {
  updateBasketItem as updateBasket,
  setBuyer,
  editDelivery,
  setAcceptTerms,
  selectPaymentMethod,
  removeRedeem,
  checkoutBasket,
  setShippingQuote,
  getAndSetShippingQuotes,
  setOrderReference,
} from '../../actions/basket';
import { updateGreeting } from '../../actions/greeting';

import {
  updateBasketItem,
  removeBasketItem,
  normalizeBuyer,
  flattenRecipientInformation,
  priceFormat,
  ucFirst,
  debounce,
  sortDeliveryMethods,
  urlPrefix,
  isAlphabetic,
} from '../../helpers';

import history from '../../history';

import {
  addGreetingMessages,
  addWrappingMessages,
  basketMessages as messages,
  formMessages,
  productMessages,
  notificationMessages,
  redeemMessages,
} from '../../defineMessages';

import Page from '../../components/Page';
import Grid from '../../components/Grid';
import Label from '../../components/Label';
import NumericInput from '../../components/NumericInput';
import Icon from '../../components/Icon';
import List from '../../components/List';
import Clickable from '../../components/Clickable';
import Link from '../../components/Link';
import Field from '../../components/FormField';
import Modal from '../../components/Modal';
import RadioGroup from '../../components/RadioGroup';
import AddGreeting from '../../components/AddGreeting';
import Checkbox from '../../components/Checkbox';
import Radio from '../../components/Radio';
import TextInput from '../../components/TextInput';
import AddWrapping from '../../components/AddWrapping';
import Price from '../../components/Price';
import BasketIssues from '../../components/BasketIssues';

import {
  billingFieldsets,
  redeemBillingFields,
  recipientFields,
} from './fields';
import {
  setDeliveryStateHelper,
  getPhysicalProducts,
  getDeliveryFees,
  getTotalDeliveryFees,
  getTagManagerProducts,
  getNumberFixedValue,
} from './helpers';
import AutoSuggest from '../../components/AutoSuggest/AutoSuggest';
import BasketSkeleton from './BasketSkeleton/BasketSkeleton';
import ApplicationContext from '../../components/ApplicationContext';
import { sanitizerConfig } from '../../helpers/sanitizeHtml';
import DateTimeOptions from '../../components/DateTimeOptions/DateTimeOptions';
import { isDateInPast } from '../../helpers/date';

const triggerProps = {
  className: s.edit,
  naked: true,
};

class Basket extends Component {
  constructor(props) {
    super(props);
    const {
      basket: { data },
      countryCode,
    } = props;

    const quantities = data
      ? data.reduce(
          (obj, line) => ({ ...obj, [line.lineId]: line.quantity }),
          {},
        )
      : {};

    const deliveries = setDeliveryStateHelper(data, countryCode);

    this.state = {
      greetingModals: {},
      wrappingModals: {},
      quantities,
      deliveries,
      removeWrappingModalOpen: false,
      newDeliveryDate: null,
    };
    this.elementrefs = {};
  }

  componentDidMount() {
    this.getShippingQuotesForPhysicalDeliveries();
  }

  componentDidUpdate() {
    this.getShippingQuotesForPhysicalDeliveries();
  }

  getShippingQuotesForPhysicalDeliveries() {
    const {
      basket: {
        id,
        data,
        loading,
        shippingQuoteLoading,
        shippingQuoteError,
        presentationData,
      },
    } = this.props;

    if (loading || shippingQuoteLoading || shippingQuoteError || !data) {
      return false;
    }

    const shippingQuoteDeliveryIds = [
      ...new Set(
        data
          .filter(
            item =>
              item.deliveryMethod === DELIVERY_METHODS.PHYSICAL &&
              presentationData[item.productId].productType ===
                PRODUCT_TYPE.giftCard &&
              !item.shippingQuote,
          )
          .map(item => item.id),
      ),
    ];

    return this.props.getAndSetShippingQuotes(shippingQuoteDeliveryIds, id);
  }

  static getDerivedStateFromProps(props, state) {
    const {
      basket: { data },
      countryCode,
    } = props;
    if (data && data.length > 0) {
      const quantityUpdated = !Object.keys(state.quantities).every(q => {
        return data.map(line => line.lineId).includes(q);
      });

      const hasNewItems = data.length > Object.keys(state.quantities).length;

      const hasNewDeliveries =
        data.length > Object.keys(state.deliveries).length;

      if (quantityUpdated || hasNewItems || hasNewDeliveries) {
        const quantities = data.reduce(
          (obj, line) => ({ ...obj, [line.lineId]: line.quantity }),
          {},
        );

        const deliveries = setDeliveryStateHelper(data, countryCode);

        return {
          ...state,
          quantities,
          deliveries,
        };
      }
    }
    return null;
  }

  // Same function in Account. Put in helper somewhere once errors are everywhere.

  getFieldError(fieldName) {
    const {
      errors,
      intl: { formatMessage },
    } = this.props;

    if (this.state[fieldName]) {
      return {
        message: this.state[fieldName].message,
      };
    }

    if (errors) {
      const error =
        errors[fieldName] ||
        Object.values(errors).find(
          ({ path }) => path && path[path.length - 1] === fieldName,
        );
      if (error) {
        const { code, message } = error;

        this.scrollToField(fieldName);

        return {
          ...errors[fieldName],
          message: notificationMessages[code]
            ? formatMessage({ ...notificationMessages[code] })
            : message,
        };
      }
    }

    return undefined;
  }

  onDeliveryUpdate = (event, orderLine, priceCurrencySku = {}) => {
    event.preventDefault();

    if (this.deliveryFormValidation(event.target.elements, orderLine)) {
      const currentDelivery = {
        ...this.state.deliveries[orderLine],
        deliveryDate: this.state.newDeliveryDate,
      };
      const delivery = trimStringProperties(currentDelivery);
      this.updateBasketValue(orderLine, {
        ...delivery,
        ...priceCurrencySku,
        addWrapping: null,
      }).then(() => {
        const postCodeError = this.props.errors['recipientAddress.postCode'];
        if (postCodeError) {
          this.setState({
            [orderLine]: {
              ...(this.state[orderLine] || {}),
              postCode: {
                message: this.props.intl.formatMessage({
                  ...formMessages.invalidPostCode,
                }),
              },
            },
          });
        } else {
          this.setState({
            newDeliveryDate: null,
          });
        }
      });
    }
  };

  setDeliveryValue = (orderLine, name, value) =>
    this.setState({
      deliveries: {
        ...this.state.deliveries,
        [orderLine]: {
          ...this.state.deliveries[orderLine],
          [name]: value,
        },
      },
    });

  // not used?
  onDeliveryFieldFocus = (orderLine, name) =>
    this.setState({
      [orderLine]: {
        ...(this.state[orderLine] || {}),
        [name]: undefined,
      },
    });

  // not used?
  onDeliveryFieldError = (orderLine, name) =>
    this.state && this.state[orderLine] && this.state[orderLine][name]
      ? { message: this.state[orderLine][name].message }
      : undefined;

  getEditDeliveryModal = (
    orderLine,
    deliveryOptions,
    recipientOptions,
    formValues,
    valueCurrency,
    wrappings,
  ) => {
    const {
      intl: { formatMessage },
      mode,
      salesChannelGroup,
    } = this.props;

    const deliveryInState =
      this.state.deliveries && this.state.deliveries[orderLine];

    const deliveryMethodChanged =
      deliveryInState?.deliveryMethod !== formValues?.deliveryMethod;

    const skuExists =
      deliveryOptions.length &&
      deliveryOptions[deliveryInState?.deliveryMethod].some(
        d => d.sku === formValues?.stockKeepingUnit,
      );

    let showAmountInput = deliveryMethodChanged && !skuExists;
    let priceCurrencySku;

    if (deliveryMethodChanged && !skuExists && !priceCurrencySku) {
      const inventoryWithMatchingCurrency = deliveryOptions[
        deliveryInState.deliveryMethod
      ].filter(d => d.defaultPriceCurrency === valueCurrency);

      const inventoryWithMatchingCurrencyAndPrice = inventoryWithMatchingCurrency.filter(
        d => d.defaultPrice === formValues.giftcardValue,
      );

      priceCurrencySku = inventoryWithMatchingCurrencyAndPrice.reduce(
        (obj, d) => {
          return {
            ...obj,
            giftcardValue: d.defaultPrice,
            valueCurrency: d.defaultPriceCurrency,
            stockKeepingUnit: d.sku,
          };
        },
        {},
      );

      showAmountInput = !(
        priceCurrencySku && Object.keys(priceCurrencySku).length !== 0
      );
    }

    const hasWrappingsAndIsNotPhysical =
      wrappings?.length > 0 &&
      deliveryInState?.deliveryMethod !== DELIVERY_METHODS.PHYSICAL;

    return (
      <Modal
        title={formatMessage({ ...messages.editDeliveryType })}
        maxWidth
        className={s.modal}
        triggerProps={{
          ...triggerProps,
          label: formatMessage({ ...messages.edit }),
        }}
        onClose={() =>
          this.setState({
            newDeliveryDate: null,
            deliveries: {
              ...this.state.deliveries,
              [orderLine]: {
                ...formValues,
              },
            },
          })
        }
      >
        <form
          noValidate
          className={s.form}
          onSubmit={event => {
            this.onDeliveryUpdate(event, orderLine, priceCurrencySku);
          }}
        >
          {hasWrappingsAndIsNotPhysical &&
          this.state.removeWrappingModalOpen ? (
            <>
              <div
                className={cx(
                  s['form-wrapping-container'],
                  s['form-wrapping-container__marginTop'],
                )}
              >
                <Label tag="h3" className={cx(s['form-confirm-label'])}>
                  <FormattedMessage
                    {...addWrappingMessages.removalOfwrappings}
                  />
                </Label>
                <Label
                  tag="h3"
                  className={cx(s.title, s['form-confirm-label'])}
                >
                  <FormattedMessage {...addWrappingMessages.confirmation} />
                </Label>
              </div>
              <div className={s['form-wrapping-container']}>
                <Clickable
                  primary
                  onClick={() =>
                    this.setState({ removeWrappingModalOpen: false })
                  }
                >
                  <FormattedMessage {...addWrappingMessages.continue} />
                </Clickable>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label tag="h3" className={cx(s.title, s['form-label'])}>
                  <FormattedMessage {...formMessages.delivery} />
                </Label>
                {this.getDeliveryOptions(orderLine, deliveryOptions, wrappings)}
              </div>
              {showAmountInput &&
                this.getDeliveryAmountInput(
                  orderLine,
                  priceCurrencySku,
                  formValues,
                  deliveryOptions,
                )}
              {!(
                mode === ACCOUNT_TYPE.REDEEM || salesChannelGroup === 'Portal'
              ) && (
                <>
                  <hr className={s.divider} />
                  <div>
                    <Label tag="h3" className={cx(s.title, s['form-label'])}>
                      <FormattedMessage {...formMessages.recipient} />
                    </Label>
                    <fieldset>
                      <legend className="visually-hidden">
                        {formatMessage({ ...formMessages.recipient })}
                      </legend>
                      {this.getDeliveryFields(
                        orderLine,
                        recipientOptions,
                        valueCurrency,
                      )}
                    </fieldset>
                  </div>
                </>
              )}
              <div className={s.submit}>
                <Clickable primary type="submit">
                  <FormattedMessage {...messages.update} />
                </Clickable>
              </div>
            </>
          )}
        </form>
      </Modal>
    );
  };

  getDeliveryOptions = (orderLine, fields) => {
    const {
      intl: { formatMessage },
      mode,
    } = this.props;

    const filteredFields =
      mode !== ACCOUNT_TYPE.B2B
        ? Object.keys(fields).filter(key => key !== DELIVERY_METHODS.CSV)
        : Object.keys(fields);

    const options = filteredFields.map(key => ({
      value: key,
      label: formatMessage({ ...formMessages[key] }),
      isSelected: this.state.deliveries[orderLine].deliveryMethod === key,
      required: true,
      onChange: (name, value) => {
        if (
          this.state.removeWrappingModalOpen === false &&
          value !== DELIVERY_METHODS.PHYSICAL &&
          this.state.deliveries[orderLine].deliveryMethod ===
            DELIVERY_METHODS.PHYSICAL
        ) {
          this.setState({ removeWrappingModalOpen: true });
        }
        this.setDeliveryValue(orderLine, name, value);
      },
    }));
    const sortedOptions = sortDeliveryMethods(options);

    return (
      <>
        <Grid item xs={24} sm={mode !== ACCOUNT_TYPE.REDEEM ? 12 : 24}>
          <Field
            input={RadioGroup}
            legend={
              <Label className={s['form-label']}>
                {formatMessage({ ...formMessages.method })}
              </Label>
            }
            name="deliveryMethod"
            radioProps={sortedOptions}
            fluid
          />
        </Grid>
        {mode !== ACCOUNT_TYPE.REDEEM &&
          this.state.deliveries[orderLine].deliveryMethod !==
            DELIVERY_METHODS.PHYSICAL && (
            <Grid item xs={24} className={s.dateSection}>
              <DateTimeOptions
                initialValue={this.state.deliveries[orderLine].deliveryDate}
                onChange={value => {
                  this.setState({
                    newDeliveryDate: value,
                  });
                  this.setDeliveryValue(orderLine, 'deliveryDate', value);
                }}
              />
            </Grid>
          )}
      </>
    );
  };

  getDeliveryFields = (orderLine, fields) => {
    const {
      intl: { formatMessage },
    } = this.props;

    return (
      <Grid container spacing={16}>
        {fields.map(
          ({
            label,
            name,
            type,
            component,
            required,
            responsive,
            placeholder,
            ...rest
          }) => (
            <Grid key={name} item {...responsive}>
              <Field
                input={component}
                type={type}
                name={name}
                label={formatMessage({ ...formMessages[label] })}
                showLabel
                required={required}
                value={this.state.deliveries[orderLine][name]}
                onChange={value =>
                  this.setDeliveryValue(orderLine, name, value)
                }
                onFocus={() => {
                  this.setState({
                    [orderLine]: {
                      ...(this.state[orderLine] || {}),
                      [name]: undefined,
                    },
                  });
                }}
                hasError={
                  this.state &&
                  this.state[orderLine] &&
                  this.state[orderLine][name]
                    ? { message: this.state[orderLine][name].message }
                    : undefined
                }
                placeholder={
                  typeof placeholder === 'object'
                    ? formatMessage(placeholder)
                    : placeholder
                }
                {...rest}
              />
            </Grid>
          ),
        )}
      </Grid>
    );
  };

  getDeliveryAmountInput = (
    orderLine,
    priceCurrencySku,
    formValues,
    deliveryMethods,
  ) => {
    const {
      intl: { formatMessage },
    } = this.props;
    const deliveryInState = this.state.deliveries[orderLine];

    return (
      <>
        <hr className={s.divider} />
        <div>
          <p>{formatMessage({ ...messages.selectNewAmount })}</p>
          <Field
            id={`delivery-amount-${orderLine}`}
            input={AutoSuggest}
            basic
            fluid
            required
            label={formatMessage({ ...formMessages.amount })}
            value={priceCurrencySku.giftcardValue || ''}
            valueLabel={
              deliveryInState.stockKeepingUnit !==
                formValues.stockKeepingUnit &&
              this.getAmountValueLabel(
                orderLine,
                deliveryMethods,
                deliveryInState.valueCurrency,
              )
            }
            name="amountValue"
            options={this.getAmountOptions(
              formValues.productId,
              deliveryInState.deliveryMethod,
            )}
            onChange={({ sku, currency, value, inStock }) => {
              if (inStock) {
                this.setState({
                  deliveries: {
                    ...this.state.deliveries,
                    [orderLine]: {
                      ...this.state.deliveries[orderLine],
                      giftcardValue: value,
                      valueCurrency: currency,
                      stockKeepingUnit: sku,
                    },
                  },
                });
              }
            }}
            hasError={
              this.state &&
              this.state[orderLine] &&
              this.state[orderLine].amountValue
                ? { message: this.state[orderLine].amountValue.message }
                : undefined
            }
          />
        </div>
      </>
    );
  };

  getAmountValueLabel = (orderLine, deliveryMethods, valueCurrency) => {
    const {
      basket: { paymentCurrency },
      intl: { formatNumber },
    } = this.props;
    const selectedOption = deliveryMethods[
      this.state.deliveries[orderLine].deliveryMethod
    ].reduce((prev, curr) =>
      Math.abs(
        curr.defaultPrice - this.state.deliveries[orderLine].giftcardValue,
      ) <
      Math.abs(
        prev.defaultPrice - this.state.deliveries[orderLine].giftcardValue,
      )
        ? curr
        : prev,
    );

    const price = selectedOption.defaultPrice;

    const { attributes = [] } = selectedOption;

    const valueLabelAttributes = attributes.reduce(
      (sum, { name }) => (sum === '' ? name : `${sum}, ${name}`),
      '',
    );

    return priceFormat({
      value: price,
      currency: valueCurrency,
      defaultCurrency: paymentCurrency,
      formatNumber,
      prefix: valueLabelAttributes || false,
      parenthesis: !!valueLabelAttributes,
      showCurrency: true,
    });
  };

  getAmountOptions = (id, selectedDeliveryMethod) => {
    const {
      basket: { presentationData },
      intl: { formatNumber, formatMessage },
    } = this.props;
    const { deliveryMethods, stockInfo } = presentationData[id];
    if (
      typeof deliveryMethods === 'undefined' ||
      typeof deliveryMethods[selectedDeliveryMethod] === 'undefined'
    ) {
      return [];
    }

    const emptyStockMessage = formatMessage({
      ...productMessages.amountNotInStock,
    });
    return deliveryMethods[selectedDeliveryMethod].map(
      ({ defaultPrice, sku, defaultPriceCurrency, attributes }) => {
        let labelAttributes;
        if (attributes.length > 0) {
          labelAttributes = attributes.reduce(
            (sum, { name }) => (sum === '' ? name : `${sum}, ${name}`),
            '',
          );
        }

        const usedStock = stockInfo?.find(
          ({ deliveryMethod }) => deliveryMethod === selectedDeliveryMethod,
        );

        const { inStock } = usedStock?.skuStockInfo?.find(
          ({ sku: stockSku }) => stockSku === sku,
        ) || { inStock: false };

        return {
          value: defaultPrice,
          inStock,
          emptyStockMessage,
          label: priceFormat({
            value: defaultPrice,
            currency: defaultPriceCurrency,
            formatNumber,
            prefix: labelAttributes || false,
            parenthesis: !!labelAttributes,
            showCurrency: true,
          }),
          currency: defaultPriceCurrency,
          sku,
        };
      },
    );
  };

  getBillingFieldsets = () => {
    const {
      intl: { formatMessage },
      basket: { billingForm, data },
      mode,
    } = this.props;

    let fieldsets = billingFieldsets;
    if (mode === ACCOUNT_TYPE.REDEEM) {
      fieldsets = redeemBillingFields(data.map(x => x.deliveryMethod));
    }

    return Object.keys(fieldsets).map(key => (
      <fieldset key={key} className={s.fieldset}>
        <legend className="visually-hidden">{key}</legend>
        <Grid container spacing={16}>
          {fieldsets[key].map(
            ({
              label,
              name,
              type,
              component,
              required,
              responsive,
              placeholder,
              hasError,
              ...rest
            }) => (
              <Grid key={name} item {...responsive}>
                <Field
                  input={component}
                  key={name}
                  type={type}
                  name={name}
                  disabled={name === 'countryCode'}
                  label={formatMessage({ ...formMessages[label] })}
                  showLabel
                  required={required}
                  value={billingForm ? billingForm[name] : null}
                  innerRef={this.createRef(name)}
                  onChange={value => this.props.setBuyer(name, value)}
                  onFocus={() => this.setState({ [name]: undefined })}
                  hasError={this.getFieldError(name)}
                  placeholder={
                    typeof placeholder === 'object'
                      ? formatMessage(placeholder)
                      : placeholder
                  }
                  {...rest}
                />
              </Grid>
            ),
          )}
        </Grid>
      </fieldset>
    ));
  };

  createRef = name => {
    const elemRef = this.elementrefs[name] || React.createRef();
    this.elementrefs[name] = elemRef;
    return elemRef;
  };

  getGreetingModal = (greetingLines, lineId) => {
    const {
      intl: { formatMessage },
    } = this.props;
    const greetingLine = greetingLines.find(
      g => g.relatedToProductLine === lineId,
    );
    const greetingToken = greetingLine ? greetingLine.greetingToken : undefined;

    return (
      <Fragment>
        <Modal
          isVisible={this.isGreetingModalVisible(lineId)}
          fullScreen
          handleClick={() => this.toggleGreetingModal(lineId)}
          triggerProps={{
            ...triggerProps,
            label: formatMessage(greetingToken ? messages.edit : messages.add),
          }}
        >
          <AddGreeting
            greetingToken={greetingToken}
            closeModal={() => this.toggleGreetingModal(lineId)}
            onAdd={greeting => this.addGreetingToProduct(greeting, lineId)}
            onCancel={() => this.toggleGreetingModal(lineId)}
            onUpdate={greeting => this.updateGreeting(greeting, lineId)}
          />
        </Modal>
        {greetingToken && (
          <Clickable
            naked
            className={s.delete}
            onClick={() => this.removeGreetingFromProduct(lineId)}
            label={formatMessage(messages.delete)}
          />
        )}
      </Fragment>
    );
  };

  getGiftWrapping = (basketWrappingLines, lineId) => {
    const {
      intl: { formatMessage, locale },
      wrapping: { data: { items = [] } } = {},
    } = this.props;

    const { wrappingId, quantity } =
      basketWrappingLines.find(w => w.relatedToProductLine === lineId) || {};
    const selectedWrapping =
      wrappingId && items.find(({ id }) => id === wrappingId);

    const productHasWrapping = wrappingId && selectedWrapping;

    const { labels = {}, wrappingPriceNoTax, wrappingPriceCurrency } =
      selectedWrapping || {};

    const selectedWrapperDetails =
      productHasWrapping && `${quantity} x ${labels[locale]}`;

    return (
      <Grid
        container
        spacing={8}
        alignItems="flex-start"
        direction={productHasWrapping ? 'column' : 'row'}
      >
        <Grid item xs md="auto">
          <Label
            bold
            icon={{
              name: ICONS.GIFT,
              size: 24,
            }}
            className={s['item-actions__label']}
          >
            <FormattedMessage {...messages.giftwrapping} />
          </Label>
        </Grid>
        {productHasWrapping && (
          <Grid item xs md="auto">
            <Label subdued>
              {selectedWrapperDetails}{' '}
              <Price
                value={wrappingPriceNoTax}
                currency={wrappingPriceCurrency}
              />
            </Label>
          </Grid>
        )}
        <Grid item>
          <AddWrapping
            modalProps={{
              title: formatMessage({ ...productMessages.addWrappingTitle }),
              triggerProps: {
                ...triggerProps,
                label: formatMessage(
                  productHasWrapping ? messages.edit : messages.add,
                ),
                isVisible: this.isGreetingModalVisible(lineId),
                onClose: () => this.toggleWrappingModal(lineId),
              },
            }}
            submitProps={{
              label: formatMessage({ ...addGreetingMessages.saveAndContinue }),
            }}
            wrappingDescription={formatMessage({
              ...productMessages.giftWrappingDescription,
            })}
            wrappingDetails={formatMessage({
              ...productMessages.giftWrappingDetails,
            })}
            defaultWrapping={
              selectedWrapping ? { ...selectedWrapping, quantity } : items[0]
            }
            onAdd={wrapping => {
              this.setState({ removeWrappingModalOpen: false });
              this.addGiftWrappingToProduct(wrapping, lineId);
            }}
          />
        </Grid>
      </Grid>
    );
  };

  productHasFees = (data, lineId) => {
    const productFees =
      data && data.filter(item => item.relatedToProductLine === lineId);
    return productFees && productFees.length > 0;
  };

  getProductRelatedWrappings = (wrappings = [], lineId) => {
    const filteredWrappings = wrappings.filter(
      wrapping => wrapping.relatedToProductLine === lineId,
    );

    return {
      productHasWrappings: filteredWrappings.length > 0,
      relatedWrappings: filteredWrappings,
    };
  };

  getProductFees = (data, lineId) => {
    const {
      intl: { formatMessage },
    } = this.props;
    let productFees = [];
    productFees =
      data && data.filter(item => item.relatedToProductLine === lineId);
    return (
      <>
        {productFees.map(
          ({
            labels,
            totalPaymentAmountWithDiscountNoTax,
            paymentTaxAmount,
          }) => (
            <List key={`fee-${lineId}`} item component="li" className={s.fee}>
              <span className={s.fee__label}>
                {labels[this.props.locale] || labels.en}{' '}
                {paymentTaxAmount > 0
                  ? formatMessage(messages.includingTax)
                  : ''}
              </span>
              <Price
                prefix="+"
                value={
                  paymentTaxAmount > 0
                    ? totalPaymentAmountWithDiscountNoTax + paymentTaxAmount
                    : totalPaymentAmountWithDiscountNoTax
                }
                currency={this.props.basket.paymentCurrency}
              />
            </List>
          ),
        )}
      </>
    );
  };

  getWrappingPrices = (relatedWrappings = [], lineId) => {
    const {
      intl: { formatMessage },
      wrapping: { data: { items: availableWrappings = [] } = {} },
    } = this.props;

    return (
      <>
        {relatedWrappings.map(
          ({
            totalPaymentAmountWithDiscountNoTax,
            paymentTaxAmount,
            wrappingId,
          }) => {
            const { labels = {} } =
              availableWrappings.find(({ id }) => id === wrappingId) || {};

            return (
              <List
                key={`fee-${lineId}-${wrappingId}`}
                item
                component="li"
                className={s.fee}
              >
                <span className={s.fee__label}>
                  {labels[this.props.locale] || labels.en}{' '}
                  {paymentTaxAmount > 0
                    ? formatMessage(messages.includingTax)
                    : ''}
                </span>
                <Price
                  prefix="+"
                  value={
                    paymentTaxAmount > 0
                      ? totalPaymentAmountWithDiscountNoTax + paymentTaxAmount
                      : totalPaymentAmountWithDiscountNoTax
                  }
                  currency={this.props.basket.paymentCurrency}
                />
              </List>
            );
          },
        )}
      </>
    );
  };

  getDiscounts = (
    discountTitles,
    totalPaymentDiscountAmount,
    totalPaymentAmountWithDiscountNoTax,
  ) => {
    const totalPayment =
      totalPaymentDiscountAmount + totalPaymentAmountWithDiscountNoTax;
    let percentage = 0;
    percentage = (
      <FormattedNumber
        value={totalPaymentDiscountAmount / totalPayment}
        // eslint-disable-next-line react/style-prop-object
        style="percent"
        maximumFractionDigits={2}
      />
    );
    return (
      <List item component="li" className={s.fee}>
        <span className={s.fee__label}>
          {discountTitles[this.props.locale] || discountTitles.en} ({percentage}
          )
        </span>
        <Price
          prefix="-"
          value={totalPaymentDiscountAmount}
          currency={this.props.basket.paymentCurrency}
        />
      </List>
    );
  };

  getProductTotalWithDiscountFeesAndWrappings = (
    totalProductPaymentAmountWithDiscountNoTax,
    totalProductPaymentTaxAmount,
    fees,
    wrappings,
  ) => {
    let totalFeesPaymentAmount = 0;
    if (fees && fees.length > 0) {
      fees
        .filter(item => item.relatedToProductLine !== null)
        .map(
          ({ totalPaymentAmountWithDiscountNoTax, totalPaymentTaxAmount }) => {
            totalFeesPaymentAmount +=
              totalPaymentAmountWithDiscountNoTax + totalPaymentTaxAmount;
            return totalFeesPaymentAmount;
          },
        );
    }
    let totalWrappingsPaymentAmount = 0;
    if (wrappings && wrappings.length > 0) {
      wrappings
        .filter(item => item.relatedToProductLine !== null)
        .forEach(
          ({ totalPaymentAmountWithDiscountNoTax, totalPaymentTaxAmount }) => {
            totalWrappingsPaymentAmount +=
              totalPaymentAmountWithDiscountNoTax + totalPaymentTaxAmount;
          },
        );
    }
    return (
      totalProductPaymentAmountWithDiscountNoTax +
      totalProductPaymentTaxAmount +
      totalFeesPaymentAmount +
      totalWrappingsPaymentAmount
    );
  };

  getBasketSubtotal = data => {
    const total = data
      .map(
        ({
          totalPaymentAmountWithDiscountNoTax,
          totalPaymentTaxAmount,
          fees,
          wrappings,
        }) => {
          return this.getProductTotalWithDiscountFeesAndWrappings(
            totalPaymentAmountWithDiscountNoTax,
            totalPaymentTaxAmount,
            fees,
            wrappings,
          );
        },
      )
      .reduce((acc, curr) => acc + curr);
    return total;
  };

  getBasketFees = () => {
    const {
      intl: { formatMessage },
      basket: { shippingQuoteLoading, data },
    } = this.props;

    const basketFees = getDeliveryFees(data);

    const labelPricePairs = basketFees.reduce(
      (
        res,
        { labels, totalPaymentAmountWithDiscountNoTax, paymentTaxAmount },
      ) => {
        const label = labels[this.props.locale] || labels.en;
        const pair = res.find(obj => obj.key === label);
        if (pair) {
          pair.price += totalPaymentAmountWithDiscountNoTax + paymentTaxAmount;
        } else {
          res.push({
            key: label,
            label: `${label}${
              paymentTaxAmount > 0
                ? ` ${formatMessage(messages.includingTax)}`
                : ''
            }`,
            price: totalPaymentAmountWithDiscountNoTax + paymentTaxAmount,
          });
        }
        return res;
      },
      [],
    );

    return (
      <>
        {shippingQuoteLoading && (
          <Label>{formatMessage(messages.shippingQuoteLoading)}</Label>
        )}
        {labelPricePairs.map(({ key, label, price }) => (
          <div key={key} className={s.fee}>
            <Label className={s.fee__label}>{label}</Label>
            <Label>
              <Price
                value={price}
                currency={this.props.basket.paymentCurrency}
              />
            </Label>
          </div>
        ))}
      </>
    );
  };

  getAcceptTerms = () => {
    const {
      basket: { acceptTerms },
      intl: { formatMessage },
    } = this.props;

    return (
      <Field
        input={Checkbox}
        name="acceptTerms"
        label={
          <Fragment>
            <Label
              label={formatMessage(messages.agreeToTerms)}
              id="acceptTerms"
            />{' '}
            <Link to={TERMS_AND_CONDITIONS_PATH} className={s.edit}>
              {formatMessage(messages.gogiftsTerms)}
            </Link>
          </Fragment>
        }
        showLabel
        required
        checked={acceptTerms}
        innerRef={this.createRef('acceptTerms')}
        onChange={() => this.props.setAcceptTerms(!acceptTerms)}
        onFocus={() => this.setState({ acceptTerms: undefined })}
        hasError={
          this.state && this.state.acceptTerms
            ? { message: this.state.acceptTerms.message }
            : undefined
        }
      />
    );
  };

  getPaymentOptions = () => {
    const {
      basket: { paymentMethod, salesChannelId },
      intl: { formatMessage },
      user,
      page: {
        basketTrustlyLinkLabel,
        basketTrustlyLinkUrl,
        basketTrustlyLogo,
        basketAltapayLinkLabel,
        basketAltapayLinkUrl,
        basketAltapayLogo,
        adyenTrustlyLinkLabel,
        adyenTrustlyLinkUrl,
        adyenTrustlyLogo,
      },
      countryPaymentMethods = [],
    } = this.props;
    const {
      config: { paymentMethods },
    } = this.context;
    const { departmentId, approvedForInvoice = false, account } = user || {};

    const isTrustlyEnabled =
      countryPaymentMethods.includes(PAYMENT_METHODS.TRUSTLY) &&
      paymentMethods.includes(PAYMENT_METHODS.TRUSTLY);
    const isAdyenEnabled =
      countryPaymentMethods.includes(PAYMENT_METHODS.ADYEN) &&
      paymentMethods.includes(PAYMENT_METHODS.TRUSTLY);
    return (
      <Grid container spacing={24} alignItems="stretch">
        {departmentId && paymentMethods.includes(PAYMENT_METHODS.PSP) && (
          <Grid item xs={24} md={8}>
            <Field
              input={Radio}
              name="paymentMethod"
              label={formatMessage(messages.invoicePayment)}
              showLabel
              disabled={
                !approvedForInvoice ||
                (account && account.status === DEPARTMENT_USER_STATUS.BLOCKED)
              }
              blockBtn
              isSelected={paymentMethod === PAYMENT_METHODS.INVOICE}
              onChange={() =>
                this.props.selectPaymentMethod(PAYMENT_METHODS.INVOICE)
              }
              value={PAYMENT_METHODS.INVOICE}
              hasError={
                this.state && this.state.paymentMethod
                  ? { message: this.state.paymentMethod.message }
                  : undefined
              }
              className={s.radio}
            />
          </Grid>
        )}
        {paymentMethods.includes(PAYMENT_METHODS.PSP) && (
          <Grid item xs={24} md={8}>
            <Field
              input={Radio}
              name="paymentMethod"
              label={formatMessage(messages.paymentServiceProvider)}
              showLabel
              link={{
                label: basketAltapayLinkLabel,
                href: basketAltapayLinkUrl,
              }}
              image={{
                src: basketAltapayLogo || undefined,
                alt: `${formatMessage(messages.paymentServiceProvider)} logo`,
              }}
              blockBtn
              isSelected={
                paymentMethod === PAYMENT_METHODS.PSP || !paymentMethod
              }
              onChange={() =>
                this.props.selectPaymentMethod(PAYMENT_METHODS.PSP)
              }
              value={PAYMENT_METHODS.PSP}
              hasError={
                this.state && this.state.paymentMethod
                  ? { message: this.state.paymentMethod.message }
                  : undefined
              }
              className={s.radio}
            />
          </Grid>
        )}
        {isTrustlyEnabled && (
          <Grid item xs={24} md={8}>
            <Field
              input={Radio}
              name="paymentMethod"
              label={formatMessage(messages.trustly)}
              showLabel
              link={{
                label: basketTrustlyLinkLabel,
                href: basketTrustlyLinkUrl,
              }}
              image={{
                src: basketTrustlyLogo || undefined,
                alt: `${formatMessage(messages.trustly)} logo`,
              }}
              blockBtn
              isSelected={paymentMethod === PAYMENT_METHODS.TRUSTLY}
              onChange={() =>
                this.props.selectPaymentMethod(PAYMENT_METHODS.TRUSTLY)
              }
              value={PAYMENT_METHODS.TRUSTLY}
              hasError={
                this.state && this.state.paymentMethod
                  ? { message: this.state.paymentMethod.message }
                  : undefined
              }
              className={s.radio}
            />
          </Grid>
        )}
        {isAdyenEnabled && (
          <Grid item xs={24} md={8}>
            <Field
              input={Radio}
              name="paymentMethod"
              label={formatMessage(messages.adyen)}
              showLabel
              link={{
                label: adyenTrustlyLinkLabel,
                href: adyenTrustlyLinkUrl,
              }}
              image={{
                src: adyenTrustlyLogo || undefined,
                alt: `${formatMessage(messages.adyen)} logo`,
              }}
              blockBtn
              isSelected={paymentMethod === PAYMENT_METHODS.ADYEN}
              onChange={() =>
                this.props.selectPaymentMethod(PAYMENT_METHODS.ADYEN)
              }
              value={PAYMENT_METHODS.ADYEN}
              hasError={
                this.state && this.state.paymentMethod
                  ? { message: this.state.paymentMethod.message }
                  : undefined
              }
              className={s.radio}
            />
          </Grid>
        )}
        {salesChannelId === SALES_CHANNELS.B2C && (
          <Grid item xs={24} md={8}>
            <Clickable
              fluid
              className={s.paymentMethodRedeem}
              link={<Link to={REDEEM_PATH} />}
            >
              <FormattedMessage {...messages.redeemButton} />
            </Clickable>
          </Grid>
        )}
      </Grid>
    );
  };

  getReference = () => {
    const {
      user: {
        account: {
          department: { reference: defaultReference, isReferenceRequired },
        } = {},
      } = {},
      basket: { reference } = {},
    } = this.props;

    return (
      <Grid container spacing={24} className={s.fieldset}>
        <Grid item xs={24} sm={9}>
          <Field
            input={TextInput}
            name="reference"
            showLabel
            defaultValue={reference || defaultReference}
            onChange={value => this.props.setOrderReference(value)}
            onFocus={() => this.setState({ reference: undefined })}
            hasError={
              this.state && this.state.reference
                ? { message: this.state.reference.message }
                : undefined
            }
            required={isReferenceRequired}
          />
        </Grid>
      </Grid>
    );
  };

  checkoutFormValidation = elements => {
    let isValid = true;
    let didScroll = false;
    for (let i = 0; i < elements.length; i += 1) {
      const { name, value, type, checked } = elements[i];
      if (
        elements[i].hasAttribute('required') &&
        (value === '' || (type === 'checkbox' && checked !== true))
      ) {
        this.setState({
          [name]: {
            message: this.props.intl.formatMessage({
              ...formMessages.required,
            }),
          },
        });
        isValid = false;

        if (!didScroll) {
          this.scrollToField(name);
          didScroll = true;
        }
      }

      if (name === 'name' && !isAlphabetic(value)) {
        this.setState({
          name: {
            message: this.props.intl.formatMessage({
              ...formMessages.invalidName,
            }),
          },
        });
        isValid = false;

        if (!didScroll) {
          this.scrollToField(name);
          didScroll = true;
        }
      }
    }
    return isValid;
  };

  deliveryFormValidation = (elements, orderLine) => {
    let isValid = true;
    for (let i = 0; i < elements.length; i += 1) {
      if (elements[i].hasAttribute('required') && elements[i].value === '') {
        const elementName = elements[i].name;
        this.setState({
          [orderLine]: {
            ...(this.state[orderLine] || {}),
            [elementName]: {
              message: this.props.intl.formatMessage({
                ...formMessages.required,
              }),
            },
          },
        });
        isValid = false;
      }
    }

    // name field validation
    if (elements.recipientName && !isAlphabetic(elements.recipientName.value)) {
      this.setState({
        [orderLine]: {
          ...(this.state[orderLine] || {}),
          recipientName: {
            message: this.props.intl.formatMessage({
              ...formMessages.invalidName,
            }),
          },
        },
      });
      isValid = false;
    }
    // repeat fields validation
    if (
      elements.recipientEmailRepeat &&
      elements.recipientEmail.value !== elements.recipientEmailRepeat.value
    ) {
      this.setState({
        [orderLine]: {
          ...(this.state[orderLine] || {}),
          recipientEmailRepeat: {
            message: this.props.intl.formatMessage({
              ...formMessages.EmailNotMatching,
            }),
          },
        },
      });
      isValid = false;
    }

    if (
      elements.recipientPhoneRepeat &&
      elements.recipientPhone.value !== elements.recipientPhoneRepeat.value
    ) {
      this.setState({
        [orderLine]: {
          ...(this.state[orderLine] || {}),
          recipientPhoneRepeat: {
            message: this.props.intl.formatMessage({
              ...formMessages.PhoneNotMatching,
            }),
          },
        },
      });
      isValid = false;
    }

    return isValid;
  };

  toggleGreetingModal = lineId => {
    const { greetingModals } = this.state;
    this.setState({
      greetingModals: {
        ...greetingModals,
        [lineId]: !greetingModals[lineId],
      },
    });
  };

  toggleWrappingModal = lineId => {
    const { wrappingModals } = this.state;
    this.setState({
      wrappingModals: {
        ...wrappingModals,
        [lineId]: !wrappingModals[lineId],
      },
    });
  };

  isGreetingModalVisible = lineId => {
    const { greetingModals } = this.state;
    return !!greetingModals[lineId];
  };

  addGreetingToProduct = (addGreeting, index) => {
    const { data, paymentCurrency } = this.props.basket;
    const addProducts = updateBasketItem(data, index, {
      addGreeting: { ...addGreeting },
    });
    this.props.updateBasket({ addProducts, paymentCurrency });
    this.toggleGreetingModal(index);
  };

  addGiftWrappingToProduct = (addWrapping, index) => {
    const { data, paymentCurrency } = this.props.basket;
    const addProducts = updateBasketItem(data, index, {
      addWrapping,
    });

    this.props.updateBasket({ addProducts, paymentCurrency });
    this.toggleWrappingModal(index);
  };

  updateGreeting = (addGreeting, index) => {
    const { id } = this.props.basket;
    this.props.updateGreeting({
      ...addGreeting,
      basketId: id,
    });
    this.toggleGreetingModal(index);
  };

  removeGreetingFromProduct = index => {
    const { data, paymentCurrency } = this.props.basket;
    const addProducts = updateBasketItem(data, index, { addGreeting: null });
    this.props.updateBasket({ addProducts, paymentCurrency });
  };

  onQuantityChange = ({ target }, lineId) => {
    this.setState(
      {
        ...this.state,
        quantities: {
          ...this.state.quantities,
          [lineId]: target.value || 1,
        },
      },
      () => {
        this.updateBasketValue(lineId, {
          quantity: this.state.quantities[lineId],
        });
      },
    );
  };

  updateBasketValue = (index, value) => {
    const { data, presentationData, paymentCurrency } = this.props.basket;
    const addProducts = value
      ? updateBasketItem(data, index, { ...value })
      : removeBasketItem(data, index);

    const item = data.find(product => product.lineId === index);
    const {
      presentations: { productTitle },
    } = presentationData[item.productId];

    const product = {
      name: productTitle,
      currencyCode: item.valueCurrency,
      id: item.id,
      price: item.totalPriceWithDiscountNoTax,
      quantity: item.quantity,
    };

    if (!value) {
      this.gtmRemoveFromCartEvent(product);
    } else if (item.quantity > value.quantity) {
      const numberOfItemsRemoved = item.quantity - value.quantity;
      const productItem = {
        ...product,
        price: item.priceWithDiscountNoTax * numberOfItemsRemoved,
        quantity: numberOfItemsRemoved,
      };
      this.gtmRemoveFromCartEvent(productItem);
    } else {
      const numberOfItemsAdded = value.quantity - item.quantity;
      const productItem = {
        ...product,
        price: item.priceWithDiscountNoTax * numberOfItemsAdded,
        quantity: numberOfItemsAdded,
      };
      this.gtmAddToCartEvent(productItem);
    }
    return this.props.updateBasket({ addProducts, paymentCurrency });
  };

  gtmAddToCartEvent = product => {
    addToCartEvent({
      eventData: {
        currencyCode: product.currencyCode,
        add: {
          products: [
            {
              id: product.id,
              name: product.name,
              price: product.price,
              quantity: product.quantity,
            },
          ],
        },
      },
    });
  };

  gtmRemoveFromCartEvent = product => {
    removeFromCartEvent({
      eventData: {
        currencyCode: product.currencyCode,
        remove: {
          products: [
            {
              id: product.id,
              name: product.name,
              price: product.price,
              quantity: product.quantity,
            },
          ],
        },
      },
    });
  };

  removeVoucher = async id => {
    await this.props.removeRedeem({ id });
  };

  copyFromRecipientInformation = event => {
    const {
      basket: { data, billingForm },
    } = this.props;

    this.props.setBuyer('copyFromRecipient', event.target.checked);

    if (!event.target.checked) {
      Object.keys(billingFieldsets).forEach(key => {
        billingFieldsets[key].forEach(({ name }) => {
          if (name !== 'countryCode') {
            this.props.setBuyer(name, '');
          }
        });
      });
    } else {
      if (!data) return;

      const {
        recipientName,
        recipientEmail,
        recipientPhone,
        recipientAddress,
      } = flattenRecipientInformation(data);

      const updated = {
        name: recipientName,
        email: recipientEmail,
        phone: recipientPhone,
        ...recipientAddress,
      };

      Object.keys(updated).forEach(key => {
        if (!billingForm[key]) {
          // Physical products have "MockText" set as default for 'city' and 'line1' because backend wont accept empty address lines. Delivery address will be set in pickup point step.
          this.props.setBuyer(
            key,
            updated[key] === MOCK_TEXT ? '' : updated[key],
          );
        }
      });
    }
  };

  // eslint-disable-next-line react/sort-comp
  scrollToField(fieldName, duration = 500) {
    const inputElem =
      this.elementrefs[fieldName] && this.elementrefs[fieldName].current;

    if (!inputElem || this.isScrolling) {
      this.isScrolling = false;
      return;
    }

    this.isScrolling = true;

    setTimeout(() => {
      this.isScrolling = false;
    }, duration);

    inputElem.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      duration,
    });
  }

  checkoutBasket(event) {
    if (event) {
      event.preventDefault();
    }
    if (!this.checkoutFormValidation(event.target.elements)) {
      return false;
    }
    const {
      basket: { billingForm, paymentMethod, reference, data, presentationData },
      user,
      countryCode,
      locale,
      currency,
    } = this.props;

    const physicalProducts = getPhysicalProducts(data, presentationData);

    const buyer = normalizeBuyer({
      ...billingForm,
      accountId: (user && user.departmentId) || null,
      accountType: (user && user.type) || null,
    });
    const finalReference =
      user && user.account.department.isReferenceRequired
        ? reference || user.account.department.reference
        : reference;

    if (physicalProducts.length) {
      history.push(
        urlPrefix('/basket/delivery', { countryCode, locale, currency }),
      );
    } else {
      this.props.checkoutBasket(buyer, paymentMethod, finalReference);
    }

    this.triggerCheckoutEcommerceEvent();

    return true;
  }

  triggerCheckoutEcommerceEvent() {
    const {
      basket: {
        data,
        presentationData,
        totalPaymentAmountWithTax,
        totalTaxAmount,
        salesChannelId,
      },
      currency,
    } = this.props;

    if (
      salesChannelId === SALES_CHANNELS.B2B ||
      salesChannelId === SALES_CHANNELS.B2C
    ) {
      checkoutEvent({
        eventData: {
          currencyCode: currency,
          checkout: {
            actionField: {
              step: 1,
              revenue: getNumberFixedValue(totalPaymentAmountWithTax),
              tax: getNumberFixedValue(totalTaxAmount),
              shipping: getNumberFixedValue(getTotalDeliveryFees(data)),
            },
            products: getTagManagerProducts(data, presentationData),
          },
        },
      });
    }
  }

  static contextType = ApplicationContext;

  getBasketDisclaimer = () => {
    const { page } = this.props;
    return page.basketPhysicalProductDisclaimer ? (
      <div
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(
            page.basketPhysicalProductDisclaimer,
            sanitizerConfig,
          ),
        }}
      />
    ) : null;
  };

  render() {
    const {
      basket: {
        totalRedeemAmount,
        totalVoucherAmount,
        totalPaymentAmountWithTax,
        paymentCurrency,
        data,
        itemsCount,
        presentationData,
        deliveryForms,
        issues,
        vouchers,
        billingForm,
        shippingQuoteLoading,
        salesChannelId,
      },
      user,
      mode,
      isBottom,
      isDesktop,
      intl: { formatMessage },
      loading,
      useRedeemLabels,
      showServiceMessage,
      salesChannelGroup,
    } = this.props;
    const hasItems = itemsCount > 0;
    const continueLabelId = useRedeemLabels
      ? 'continuePaymentRedeem'
      : 'continuePayment';
    const continueLabel = formatMessage({
      ...messages[continueLabelId],
    });

    const physicalProducts = getPhysicalProducts(data, presentationData);

    const hasPhysicalProducts = physicalProducts.length > 0;

    return (
      <Page>
        {loading ? (
          <BasketSkeleton showServiceMessage={showServiceMessage} />
        ) : (
          <>
            {hasItems && (
              <aside
                className={cx(
                  s.sidebar,
                  showServiceMessage && s['sidebar--showServiceMessage'],
                  isBottom && s['sidebar--isBottom'],
                )}
              >
                <div className={s.panel}>
                  <div className={s.header}>
                    <Label className={s.header__title}>
                      <FormattedMessage {...messages.orderSummary} />
                    </Label>
                  </div>
                  <div className={s.total}>
                    <div className={s.fee}>
                      <Label className={s.fee__label}>
                        <FormattedMessage
                          values={{ count: itemsCount || 0 }}
                          {...messages.subtotalItems}
                        />
                      </Label>
                      <Label>
                        <Price
                          value={this.getBasketSubtotal(data)}
                          currency={paymentCurrency}
                        />
                      </Label>
                    </div>
                    {hasPhysicalProducts && this.getBasketDisclaimer()}
                    {this.getBasketFees()}
                    {vouchers && vouchers.length > 0 && (
                      <>
                        {vouchers.map(
                          ({ id, expiresAt, paymentAmount, voucherMasked }) => (
                            <Fragment key={id}>
                              <hr />
                              <div className={s.fee}>
                                <Label className={s.fee__label}>
                                  {voucherMasked}
                                </Label>
                                <Label>
                                  -&nbsp;
                                  <Price
                                    value={paymentAmount}
                                    currency={paymentCurrency}
                                  />
                                </Label>
                              </div>
                              <div className={s.fee}>
                                <Label className={s.fee__label}>
                                  <FormattedMessage
                                    {...redeemMessages.redeemOverviewExpiry}
                                  />
                                  :{' '}
                                  <FormattedDate
                                    value={expiresAt}
                                    day="numeric"
                                    month="2-digit"
                                    year="numeric"
                                  />
                                </Label>
                                <Label>
                                  <Clickable
                                    naked
                                    className={s.remove__button}
                                    onClick={() => this.removeVoucher(id)}
                                    label={formatMessage({
                                      ...redeemMessages.redeemOverviewRemove,
                                    })}
                                  />
                                </Label>
                              </div>
                            </Fragment>
                          ),
                        )}
                      </>
                    )}
                    <hr />
                    <div className={cx(s.fee, s['fee--total'])}>
                      <Label bold className={s.fee__label}>
                        <FormattedMessage {...messages.total} />:
                      </Label>
                      <Label bold>
                        <Price
                          value={totalPaymentAmountWithTax}
                          currency={paymentCurrency}
                        />
                      </Label>
                    </div>
                    {totalRedeemAmount > 0 && (
                      <Fragment>
                        <hr />
                        <div className={s.fee}>
                          <Label bold className={s.fee__label}>
                            <FormattedMessage {...messages.amountRemaining} />:
                          </Label>
                          <Label>
                            <Price
                              value={totalVoucherAmount - totalRedeemAmount}
                              currency={paymentCurrency}
                            />
                          </Label>
                        </div>
                      </Fragment>
                    )}
                    <hr />
                    <Grid
                      container
                      spacing={16}
                      wrap="wrap"
                      alignItems="center"
                    >
                      <Grid item xs={24}>
                        {this.getAcceptTerms()}
                      </Grid>
                      <Grid item xs={24}>
                        <Clickable
                          id="checkoutButton"
                          primary
                          fluid
                          tag="label"
                          htmlFor="submit"
                          label={
                            hasPhysicalProducts
                              ? formatMessage({
                                  ...messages.chooseDeliveryOption,
                                })
                              : continueLabel
                          }
                        />
                      </Grid>
                      {(salesChannelId === SALES_CHANNELS.REDEEM ||
                        salesChannelId === SALES_CHANNELS.B2C) && (
                        <Grid item xs={24}>
                          <Clickable
                            basic
                            fluid
                            link={<Link to="/redeem" />}
                            label={formatMessage(
                              redeemMessages.basketAddRedeem,
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </div>
                </div>
              </aside>
            )}
            <div className={s.main}>
              {issues && issues.length ? <BasketIssues issues={issues} /> : ''}
              <div className={s.header}>
                <Label className={s.header__title}>
                  <FormattedMessage
                    values={{ count: itemsCount }}
                    {...messages.totalItems}
                  />
                </Label>
              </div>
              {hasPhysicalProducts && this.getBasketDisclaimer()}
              {hasItems && (
                <List container>
                  {data.map(
                    ({
                      deliveryDate,
                      deliveryMethod,
                      discountTitles,
                      exchangeRate,
                      fees,
                      giftcardValue,
                      greetings,
                      wrappings,
                      issue,
                      lineId,
                      productId,
                      recipientEmail,
                      recipientPhone,
                      recipientAddress,
                      totalPaymentAmountWithDiscountNoTax,
                      totalPaymentDiscountAmount,
                      totalPaymentTaxAmount,
                      valueCurrency,
                    }) => {
                      if (
                        !productId ||
                        typeof presentationData[productId] === 'undefined'
                      ) {
                        return null;
                      }

                      const {
                        presentations: { productTitle, productLogo },
                        deliveryMethods,
                        productType,
                      } = presentationData[productId];
                      const {
                        productHasWrappings,
                        relatedWrappings,
                      } = this.getProductRelatedWrappings(wrappings, lineId);
                      const productHasFees = this.productHasFees(fees, lineId);
                      const productFees =
                        productHasFees &&
                        fees.filter(
                          item => item.relatedToProductLine === lineId,
                        );
                      const productHasDiscounts =
                        totalPaymentDiscountAmount > 0;
                      const productHasFeesDiscountsOrWrappings =
                        productHasFees ||
                        productHasDiscounts ||
                        productHasWrappings;

                      const productTotalsInCurrentCurrency = this.getProductTotalWithDiscountFeesAndWrappings(
                        totalPaymentAmountWithDiscountNoTax,
                        totalPaymentTaxAmount,
                        productFees,
                        wrappings,
                      );

                      const productDeliveryMethodLabel =
                        formMessages[
                          ucFirst(this.state.deliveries[lineId].deliveryMethod)
                        ] ||
                        formMessages[
                          this.state.deliveries[lineId].deliveryMethod
                        ];

                      const deliveryMethodInfo = () => {
                        switch (deliveryMethod) {
                          case 'Sms':
                            return recipientPhone;
                          case 'Email':
                            return recipientEmail;
                          case 'Physical':
                            return `${recipientAddress.line1}, ${recipientAddress.postCode}
                              ${recipientAddress.city}`;
                          default:
                            return null;
                        }
                      };

                      return (
                        <List
                          item
                          component="li"
                          key={lineId}
                          className={cx(s.panel, {
                            [s.panel__hasIssue]: issue,
                          })}
                        >
                          <div className={s.body}>
                            <Grid container className={s.container}>
                              <Grid
                                item
                                xs={12}
                                sm
                                md={6}
                                className={s['item-title']}
                              >
                                <div
                                  className={cx(
                                    s.field,
                                    s['field--first-child'],
                                  )}
                                >
                                  <div className={cx(s.head, 'hidden-sm')}>
                                    <Label bold>
                                      <FormattedMessage {...messages.product} />
                                    </Label>
                                  </div>
                                  <div
                                    className={s.content}
                                    style={{ textAlign: 'left' }}
                                  >
                                    <Label bold>{productTitle}</Label>
                                    <img
                                      className={s.image}
                                      src={
                                        productLogo &&
                                        productLogo[0] &&
                                        productLogo[0].thumbnail
                                      }
                                      alt={productTitle}
                                    />
                                  </div>
                                </div>
                              </Grid>
                              <Grid
                                item
                                xs
                                sm={24}
                                md={4}
                                className={s['item-quantity']}
                              >
                                <div className={s.field}>
                                  <div
                                    className={cx(
                                      s.head,
                                      s.field__title,
                                      'hidden-xs',
                                    )}
                                  >
                                    <Label bold>
                                      {formatMessage({
                                        ...formMessages.quantity,
                                      })}
                                    </Label>
                                  </div>
                                  <div
                                    className={cx(s.content, s.field__input)}
                                  >
                                    <Field
                                      input={NumericInput}
                                      fluid
                                      name="quantity"
                                      label={formatMessage({
                                        ...formMessages.quantity,
                                      })}
                                      format={number => `${number}`}
                                      value={this.state.quantities[lineId]}
                                      disabled={shippingQuoteLoading}
                                      onChange={debounce(ev => {
                                        this.onQuantityChange(ev, lineId);
                                      }, 250)}
                                    />
                                  </div>
                                </div>
                              </Grid>
                              <Grid
                                item
                                xs="auto"
                                md={1}
                                className={s['item-delete']}
                              >
                                <div className={cx(s.head, 'hidden-sm')}>
                                  <span style={{ visibility: 'hidden' }}>
                                    Delete
                                  </span>
                                </div>
                                <div
                                  className={cx(s.content, s['basket-delete'])}
                                >
                                  <Icon
                                    name={ICONS.DELETE}
                                    size={24}
                                    onClick={() =>
                                      this.updateBasketValue(lineId)
                                    }
                                  />
                                </div>
                              </Grid>
                              <Grid
                                item
                                xs={24}
                                md={5}
                                className={s['item-amount']}
                              >
                                <div className={s.field}>
                                  <div className={cx(s.head, s.field__title)}>
                                    {productType !==
                                    PRODUCT_TYPE.physicalProduct ? (
                                      <Label bold>
                                        <FormattedMessage
                                          {...formMessages.amount}
                                        />
                                      </Label>
                                    ) : (
                                      <span>&nbsp;</span>
                                    )}
                                  </div>
                                  <div
                                    className={cx(s.content, s.field__input)}
                                  >
                                    {productType !==
                                      PRODUCT_TYPE.physicalProduct && (
                                      <Field
                                        id={`amount-${lineId}`}
                                        input={AutoSuggest}
                                        basic
                                        fluid
                                        label={`Amount for ${lineId}`}
                                        value={giftcardValue}
                                        valueLabel={this.getAmountValueLabel(
                                          lineId,
                                          deliveryMethods,
                                          valueCurrency,
                                        )}
                                        name="value"
                                        options={this.getAmountOptions(
                                          productId,
                                          deliveryMethod,
                                        )}
                                        onChange={({
                                          sku,
                                          currency,
                                          value,
                                          inStock,
                                        }) => {
                                          if (inStock) {
                                            this.updateBasketValue(lineId, {
                                              giftcardValue: value,
                                              valueCurrency: currency,
                                              stockKeepingUnit: sku,
                                            });
                                          }
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              </Grid>
                              <Grid
                                item
                                xs={24}
                                md={2}
                                lg={2}
                                className={s['item-price']}
                              >
                                <div className={s.field}>
                                  <div className={s.head}>
                                    <Label bold>
                                      <FormattedMessage {...messages.price} />
                                    </Label>
                                  </div>
                                  <div className={s.content}>
                                    <Label bold className={s.price}>
                                      <Price
                                        value={
                                          totalPaymentAmountWithDiscountNoTax +
                                          totalPaymentDiscountAmount
                                        }
                                        currency={paymentCurrency}
                                      />
                                    </Label>
                                  </div>
                                </div>
                              </Grid>
                              <Grid
                                item
                                xs={24}
                                md={6}
                                className={s['item-delivery']}
                              >
                                <div className={s.field}>
                                  <div className={cx(s.head, 'hidden-sm')}>
                                    <Label bold>
                                      <FormattedMessage
                                        {...messages.delivery}
                                      />
                                    </Label>
                                  </div>
                                  <div
                                    className={cx(
                                      s.content,
                                      s['content--delivery'],
                                    )}
                                    style={{ textAlign: 'left' }}
                                  >
                                    <List
                                      container
                                      direction="column"
                                      spacing={4}
                                      size="caption"
                                      className={s['item-delivery__list']}
                                    >
                                      {productType ===
                                      PRODUCT_TYPE.physicalProduct ? (
                                        <List item component="li">
                                          <p
                                            className={
                                              s.physicalProductDeliveryText
                                            }
                                            dangerouslySetInnerHTML={{
                                              __html: sanitizeHtml(
                                                formatMessage({
                                                  ...messages.physicalProductDelivery,
                                                }),
                                              ),
                                            }}
                                          />
                                        </List>
                                      ) : (
                                        <>
                                          <List item component="li">
                                            <FormattedMessage
                                              {...messages.deliveryBy}
                                            />{' '}
                                            <FormattedMessage
                                              {...productDeliveryMethodLabel}
                                            />
                                          </List>
                                          {!(
                                            mode === ACCOUNT_TYPE.REDEEM ||
                                            salesChannelGroup ===
                                              SALES_CHANNELS_GROUP.PORTAL
                                          ) && (
                                            <List item component="li">
                                              {deliveryMethodInfo()}
                                            </List>
                                          )}
                                          {mode !== ACCOUNT_TYPE.REDEEM &&
                                            deliveryMethod !==
                                              DELIVERY_METHODS.PHYSICAL && (
                                              <List item component="li">
                                                {isDateInPast(deliveryDate) ? (
                                                  <FormattedMessage
                                                    {...messages.date_asap}
                                                  />
                                                ) : (
                                                  <FormattedDate
                                                    value={deliveryDate}
                                                    day="2-digit"
                                                    month="2-digit"
                                                    year="numeric"
                                                    hour="2-digit"
                                                    minute="2-digit"
                                                  />
                                                )}
                                              </List>
                                            )}
                                          <List
                                            item
                                            component="li"
                                            className={s['item-delivery__edit']}
                                          >
                                            {this.getEditDeliveryModal(
                                              lineId,
                                              deliveryMethods,
                                              recipientFields[
                                                this.state.deliveries[lineId]
                                                  .deliveryMethod
                                              ],
                                              deliveryForms[lineId],
                                              valueCurrency,
                                            )}
                                          </List>
                                        </>
                                      )}
                                    </List>
                                  </div>
                                </div>
                              </Grid>
                              <Grid
                                item
                                xs={24}
                                className={cx(s.row, s['item-actions'])}
                              >
                                <List
                                  container
                                  direction="column"
                                  className={s['item-actions__list']}
                                  size="caption"
                                >
                                  {/* {deliveryMethod === DELIVERY_METHODS.PHYSICAL &&
                                mode !== ACCOUNT_TYPE.REDEEM && (
                                  <List component="li" item>
                                    {this.getGiftWrapping(wrappings, lineId)}
                                  </List>
                                )} */}
                                  {mode !== ACCOUNT_TYPE.REDEEM &&
                                    productType !==
                                      PRODUCT_TYPE.physicalProduct && (
                                      <List item component="li">
                                        <Grid
                                          container
                                          spacing={8}
                                          alignItems="center"
                                        >
                                          <Grid item xs md="auto">
                                            <Label
                                              bold
                                              icon={{
                                                name: ICONS.GREETING,
                                                size: 24,
                                              }}
                                              className={
                                                s['item-actions__label']
                                              }
                                            >
                                              <FormattedMessage
                                                {...messages.personalizedGreeting}
                                              />
                                            </Label>
                                          </Grid>
                                          <Grid item>
                                            {this.getGreetingModal(
                                              greetings,
                                              lineId,
                                            )}
                                          </Grid>
                                        </Grid>
                                      </List>
                                    )}
                                </List>
                              </Grid>
                            </Grid>
                            {productHasFeesDiscountsOrWrappings && (
                              <Fragment>
                                {isDesktop && <hr className={s.divider} />}
                                <Grid container justify="flex-end">
                                  <Grid item xs md={6}>
                                    <List
                                      container
                                      direction="column"
                                      spacing={8}
                                    >
                                      {productHasFees &&
                                        this.getProductFees(fees, lineId)}
                                      {productHasDiscounts &&
                                        this.getDiscounts(
                                          discountTitles,
                                          totalPaymentDiscountAmount,
                                          totalPaymentAmountWithDiscountNoTax,
                                        )}
                                      {productHasWrappings &&
                                        this.getWrappingPrices(
                                          relatedWrappings,
                                          lineId,
                                        )}
                                    </List>
                                  </Grid>
                                </Grid>
                              </Fragment>
                            )}
                          </div>

                          <div className={s.footer}>
                            <Grid container justify="flex-end">
                              <Grid item>
                                <List
                                  container
                                  direction="column"
                                  spacing={4}
                                  size="caption"
                                  style={{ textAlign: 'right' }}
                                >
                                  <List item component="li">
                                    <Label bold size="body">
                                      <FormattedMessage
                                        {...messages.subtotal}
                                      />{' '}
                                      <Price
                                        value={productTotalsInCurrentCurrency}
                                        currency={paymentCurrency}
                                      />
                                    </Label>
                                  </List>
                                  {paymentCurrency !== valueCurrency && (
                                    <Fragment>
                                      <List item component="li">
                                        <Label>
                                          <Price
                                            value={
                                              productTotalsInCurrentCurrency *
                                              exchangeRate
                                            }
                                            currency={valueCurrency}
                                          />
                                        </Label>
                                      </List>
                                      <List item component="li">
                                        <div>
                                          <Label>
                                            <FormattedMessage
                                              {...messages.paymentExchangeRate}
                                            />
                                            {': '}
                                            <Price
                                              value={1}
                                              currency={paymentCurrency}
                                            />
                                            {' = '}
                                            <Price
                                              value={exchangeRate}
                                              currency={valueCurrency}
                                            />
                                          </Label>
                                        </div>
                                      </List>
                                    </Fragment>
                                  )}
                                </List>
                              </Grid>
                            </Grid>
                          </div>
                        </List>
                      );
                    },
                  )}
                </List>
              )}
              {hasItems && (
                <>
                  <div className={s.header} style={{ textAlign: 'right' }}>
                    <Label size="sm" color="#fff">
                      <FormattedMessage {...messages.total} />{' '}
                      <Price
                        value={totalPaymentAmountWithTax}
                        currency={paymentCurrency}
                      />
                    </Label>
                  </div>
                </>
              )}
              {hasItems && (
                <form onSubmit={event => this.checkoutBasket(event)} noValidate>
                  <div className={s.header}>
                    <Label className={s.header__title}>
                      <FormattedMessage {...messages.billingInformation} />
                    </Label>
                  </div>
                  <List
                    container
                    component="div"
                    spacing={24}
                    direction="column"
                  >
                    {user && user.departmentId && (
                      <List item component="div">
                        <div className={s.panel}>
                          <div className={s.panel__content}>
                            <Label tag="p" color="#c4cdd5">
                              <FormattedMessage {...messages.b2bUserAddress} />{' '}
                              <Clickable
                                naked
                                className={s.edit}
                                link={<Link to={ACCOUNT_PATH} />}
                              >
                                <FormattedMessage
                                  {...messages.b2bUserAddressLink}
                                />
                              </Clickable>
                            </Label>
                          </div>
                        </div>
                      </List>
                    )}
                    <List item component="div">
                      <div className={s.panel}>
                        <div className={s.panel__content}>
                          {!(
                            mode === ACCOUNT_TYPE.B2B ||
                            mode === ACCOUNT_TYPE.REDEEM ||
                            salesChannelGroup === SALES_CHANNELS_GROUP.PORTAL
                          ) && (
                            <Field
                              input={Checkbox}
                              label={
                                <Fragment>
                                  <Label
                                    label={formatMessage(
                                      messages.copyFromRecipientInformation,
                                    )}
                                  />
                                </Fragment>
                              }
                              showLabel
                              checked={billingForm.copyFromRecipient || false}
                              onChange={this.copyFromRecipientInformation}
                            />
                          )}
                          {this.getBillingFieldsets()}
                        </div>
                      </div>
                    </List>
                    {mode === ACCOUNT_TYPE.B2B && (
                      <List item component="div">
                        <div className={s.panel}>
                          <div className={s.panel__content}>
                            <Label
                              bold
                              size="heading"
                              className={s.panel__title}
                              tag="legend"
                            >
                              <FormattedMessage {...messages.orderReference} />
                            </Label>
                            <Label tag="p" color="#c4cdd5">
                              <FormattedMessage
                                {...messages.orderReferenceRequirement}
                              />
                            </Label>
                            {this.getReference()}
                          </div>
                        </div>
                      </List>
                    )}
                    <List item component="div">
                      <div className={s.panel}>
                        <div className={s.panel__content}>
                          <Label
                            bold
                            size="heading"
                            className={s.panel__title}
                            tag="h2"
                          >
                            <FormattedMessage
                              {...messages.termsAndConditions}
                            />
                          </Label>
                          <Label tag="p" color="#c4cdd5">
                            <FormattedMessage {...messages.treatPersonalData} />{' '}
                            <Clickable
                              naked
                              className={s.edit}
                              link={
                                <Link
                                  to={`${TERMS_AND_CONDITIONS_PATH}#personalData`}
                                />
                              }
                            >
                              <FormattedMessage {...messages.here} />
                            </Clickable>
                          </Label>

                          <List
                            container
                            direction="column"
                            spacing={8}
                            className={s.bullets}
                          >
                            <List item component="li">
                              <FormattedMessage {...messages.listItem1} />
                            </List>
                            <List item component="li">
                              <FormattedMessage {...messages.listItem2} />
                            </List>
                          </List>

                          <List
                            container
                            component="div"
                            spacing={12}
                            direction="column"
                          >
                            <List component="div" item>
                              <Label tag="p" bold>
                                <FormattedMessage {...messages.readTerms} />{' '}
                                <Clickable
                                  naked
                                  link={<Link to={TERMS_AND_CONDITIONS_PATH} />}
                                  className={s.edit}
                                >
                                  <FormattedMessage {...messages.here} />
                                </Clickable>
                              </Label>
                            </List>
                            <List component="div" item grow>
                              {this.getAcceptTerms()}
                            </List>
                          </List>
                        </div>
                      </div>
                    </List>
                    {!useRedeemLabels && (
                      <List item component="div">
                        <div className={s.panel}>
                          <div className={s.panel__content}>
                            <Label
                              bold
                              size="heading"
                              className={s.panel__title}
                              tag="legend"
                            >
                              <FormattedMessage {...messages.choosePayment} />
                            </Label>
                            {this.getPaymentOptions()}
                            {user &&
                              (!user.approvedForInvoice ||
                                user.account.status ===
                                  DEPARTMENT_USER_STATUS.BLOCKED) && (
                                <Label
                                  className={s.info}
                                  icon={{
                                    name: ICONS.INFO,
                                    size: 12,
                                    fill: '#C3C3C3',
                                  }}
                                >
                                  <FormattedMessage
                                    {...messages.paymentHelpText}
                                  />
                                </Label>
                              )}
                          </div>
                        </div>
                      </List>
                    )}
                    <List item component="div">
                      <Grid
                        container
                        justify="space-between"
                        spacing={16}
                        wrap="wrap"
                      >
                        <Grid item xs={24} sm="auto">
                          <Clickable basic fluid link={<Link to="/shop" />}>
                            <FormattedMessage {...messages.continueShopping} />
                          </Clickable>
                        </Grid>
                        <Grid item xs={24} sm="auto">
                          <Clickable
                            primary
                            fluid
                            tag="label"
                            htmlFor="submit"
                            label={
                              hasPhysicalProducts
                                ? formatMessage({
                                    ...messages.chooseDeliveryOption,
                                  })
                                : continueLabel
                            }
                          >
                            <input
                              type="submit"
                              value="Submit"
                              id="submit"
                              className="visually-hidden"
                            />
                          </Clickable>
                        </Grid>
                      </Grid>
                    </List>
                  </List>
                </form>
              )}
            </div>
          </>
        )}
      </Page>
    );
  }
}

Basket.propTypes = {
  basket: PropTypes.shape().isRequired,
  salesChannelGroup: PropTypes.string.isRequired,
  intl: PropTypes.shape().isRequired,
  isBottom: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  user: PropTypes.shape(),
  mode: PropTypes.string.isRequired,
  checkoutBasket: PropTypes.func.isRequired,
  editDelivery: PropTypes.func.isRequired,
  selectPaymentMethod: PropTypes.func.isRequired,
  setOrderReference: PropTypes.func,
  setAcceptTerms: PropTypes.func.isRequired,
  setBuyer: PropTypes.func.isRequired,
  quotes: PropTypes.shape(),
  setShippingQuote: PropTypes.func.isRequired,
  getAndSetShippingQuotes: PropTypes.func.isRequired,
  updateBasket: PropTypes.func.isRequired,
  updateGreeting: PropTypes.func.isRequired,
  removeRedeem: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  errors: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isDesktop: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  useRedeemLabels: PropTypes.bool.isRequired,
  page: PropTypes.shape().isRequired,
  countryPaymentMethods: PropTypes.arrayOf(PropTypes.string).isRequired,
  stockInfo: PropTypes.arrayOf(
    PropTypes.shape({
      deliveryMethod: PropTypes.string.isRequired,
      skuStockInfo: PropTypes.arrayOf(
        PropTypes.shape({
          inStock: PropTypes.bool.isRequired,
          sku: PropTypes.string.isRequired,
        }).isRequired,
      ).isRequired,
      allSkusOutOfStock: PropTypes.bool.isRequired,
    }),
  ),
  showServiceMessage: PropTypes.bool.isRequired,
  wrapping: PropTypes.shape({
    data: PropTypes.shape({
      items: PropTypes.arrayOf(
        PropTypes.shape({
          labels: PropTypes.shape().isRequired,
          id: PropTypes.string.isRequired,
          wrappingPriceNoTax: PropTypes.number.isRequired,
          wrappingPriceCurrency: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }).isRequired,
  }).isRequired,
};

Basket.defaultProps = {
  setOrderReference: undefined,
  user: null,
  quotes: {},
  stockInfo: null,
};

const mapState = state => {
  const { currency, locale, countryCode } = state.intl;

  return {
    basket: state.basket,
    salesChannelGroup: state.config.salesChannel.group,
    currency,
    errors: state.error.fieldErrors,
    locale,
    mode: state.user.mode,
    countryCode,
    loading: state.loading.loading || state.loading.orderLoading || false,
    useRedeemLabels:
      state.basket.totalVoucherAmount >=
      state.basket.totalPurchaseAmountWithTax,
    page: state.page.byName.basket,
    countryPaymentMethods:
      state.config.presentations[countryCode]?.countryFooterPaymentIcons?.split(
        ',',
      ) || [],
    showServiceMessage: state.message[countryCode].showMessage,
    wrapping: state.wrapping,
  };
};

const mapDispatch = {
  checkoutBasket,
  editDelivery,
  selectPaymentMethod,
  setOrderReference,
  setAcceptTerms,
  setBuyer,
  setShippingQuote,
  getAndSetShippingQuotes,
  updateBasket,
  updateGreeting,
  removeRedeem,
};

export default compose(
  injectIntl,
  connect(mapState, mapDispatch),
  withIntersectionObserver(1),
  withResponsiveWrapper(),
  withStyles(s),
)(Basket);
